/**
 * Employees: everybody utuwrk has onboarded or offboarded, as an index of
 * that work rather than a record about them (decisions.md, "Employees are an
 * index of the work, with no notes about a person").
 *
 * What is stored about a person is who they are (a name to hang their work
 * on) and the one fact no instance carries: a temp's contracted end date.
 * Everything else -- where they are in their time here, their site, their
 * department, their manager, their first and last days -- is worked out here
 * from their onboardings and offboardings, so it can never disagree with the
 * work. A stored state would be one more thing to forget to change when an
 * onboarding finishes or is cancelled.
 */

import { addCalendarDays, compareDates, endOfDay, isIsoDate, type IsoDate } from "./dates.js";
import { isOpen } from "./engine.js";
import type { Instance } from "./instance.js";
import type { Member } from "./org.js";
import { ACCESS_CHANGE, type LifecycleKind } from "./template.js";

/** The department whose cards are the hiring manager's: who holds them is the person's manager. */
export const MANAGER_DEPARTMENT = "Management";

/**
 * Where somebody is in their time here. `withdrawn` is somebody whose every
 * onboarding was cancelled -- an offer withdrawn, a no-show -- and who never
 * started.
 */
export type EmployeeState = "onboarding" | "active" | "offboarding" | "left" | "withdrawn";

/** The order a list shows them in: work in flight first, then everybody else. */
export const EMPLOYEE_STATES: readonly EmployeeState[] = [
  "onboarding",
  "offboarding",
  "active",
  "left",
  "withdrawn",
];

/** One onboarding or offboarding about the person, as the index reads it. */
export interface EmployeeWork {
  kind: LifecycleKind;
  siteId: string;
  anchorDate: IsoDate;
  createdAt: number;
  /** Every card finished, or the whole thing cancelled. */
  closed: boolean;
  instance: Instance;
}

export interface EmployeeFacts {
  state: EmployeeState;
  /** Where their latest work is. Null only for somebody with no work at all, which the index never holds. */
  siteId: string | null;
  department: string;
  /** The kind of role on their latest work, which an offboarding's conditions test again. */
  employeeType: string;
  employmentStatus: string;
  /** Whoever holds the manager's cards on their latest work; null while nobody does. */
  managerId: string | null;
  /** Their latest onboarding's start date: a withdrawn offer's as well, which is the day they did not come. */
  startDate: IsoDate | null;
  /** Their latest offboarding's last day. A cancelled one is not a last day. */
  lastDay: IsoDate | null;
}

/**
 * Everything the index says about somebody, from their work alone.
 *
 * Work in flight decides first: an open offboarding is "offboarding" even
 * while an old onboarding's day-30 card is still open. Otherwise the latest
 * work that was not cancelled says where they ended up -- an onboarding
 * finished is somebody here, an offboarding finished is somebody gone, and a
 * rehire's new onboarding comes after their old offboarding. A cancelled
 * offboarding leaves somebody where they were; with nothing but cancelled
 * onboardings, they never came.
 *
 * An access change is somebody staying, so it never moves them: it is not
 * counted for their state or their last day. It still says where they are
 * now -- their site, department and manager -- when it is their latest work.
 */
export function employeeFacts(work: readonly EmployeeWork[]): EmployeeFacts {
  const all = [...work].sort((a, b) => a.createdAt - b.createdAt);
  const live = all.filter((entry) => !entry.instance.cancelled);
  const moves = live.filter((entry) => !isAccessChange(entry.instance));
  const open = (kind: LifecycleKind) => moves.some((entry) => entry.kind === kind && !entry.closed);
  const last = moves.at(-1);

  const state: EmployeeState = open("offboarding")
    ? "offboarding"
    : open("onboarding")
      ? "onboarding"
      : last
        ? last.kind === "onboarding"
          ? "active"
          : "left"
        : all.some((entry) => entry.kind === "offboarding")
          ? "active"
          : "withdrawn";

  const latest = live.at(-1) ?? all.at(-1);
  const latestOf = (kind: LifecycleKind, among: readonly EmployeeWork[]) =>
    among.filter((entry) => entry.kind === kind).at(-1);
  const onboarding = latestOf("onboarding", live) ?? latestOf("onboarding", all);
  const offboarding = latestOf("offboarding", moves);

  return {
    state,
    siteId: latest?.siteId ?? null,
    department: latest?.instance.profile.department ?? "",
    employeeType: latest?.instance.profile.employeeType ?? "",
    employmentStatus: latest?.instance.profile.employmentStatus ?? "",
    managerId: latest ? managerOf(latest.instance) : null,
    startDate: onboarding?.anchorDate ?? null,
    lastDay: offboarding?.anchorDate ?? null,
  };
}

/** Somebody staying with some access removed: an offboarding by kind, never a departure. */
export const isAccessChange = (instance: Pick<Instance, "kind" | "profile">): boolean =>
  instance.kind === "offboarding" && instance.profile.trigger === ACCESS_CHANGE;

/** The cards on one piece of work somebody can act on now, soonest due first. Notify cards are nobody's work. */
export function openWork(instance: Instance): string[] {
  return instance.order
    .filter((key) => instance.nodes[key]?.kind === "task" && isOpen(instance.tasks[key]!))
    .sort((a, b) => instance.tasks[a]!.dueAt - instance.tasks[b]!.dueAt);
}

/** Whoever holds the first of the manager's cards that somebody holds, in the order the work flows. */
export function managerOf(instance: Instance): string | null {
  for (const key of instance.order) {
    const task = instance.tasks[key];
    if (task?.department === MANAGER_DEPARTMENT && task.assigneeId) return task.assigneeId;
  }
  return null;
}

/* ------------------------------------------------ the end-of-contract alert -- */

/** How far ahead of a temp's last contracted day HR and their manager are told (PRD 9.1). */
export const CONTRACT_ALERT_DAYS = 30;

/** The first moment the alert is owed: the start of the day, at the site, 30 days before the contract ends. */
export function contractAlertAt(endDate: IsoDate, timeZone: string): number {
  return endOfDay(addCalendarDays(endDate, -(CONTRACT_ALERT_DAYS + 1)), timeZone) + 1;
}

/**
 * Whether a temp's contract end is owed an alert at `now`. Only somebody here
 * and still a temp has a contract to decide about (somebody already leaving
 * has had it decided), and each end date is told once: extending the contract
 * is a new end date, so it earns a new alert when that one nears.
 */
export function contractAlertDue(
  employee: {
    state: EmployeeState;
    employmentStatus: string;
    endDate: IsoDate | null;
    alertedFor: IsoDate | null;
  },
  timeZone: string,
  now: number,
): boolean {
  if (employee.state !== "onboarding" && employee.state !== "active") return false;
  if (employee.employmentStatus !== "temp" || !employee.endDate) return false;
  if (employee.alertedFor === employee.endDate) return false;
  return now >= contractAlertAt(employee.endDate, timeZone);
}

/** Their manager and HR; admins when neither is left, so the alert is never dropped in silence. */
export function contractRecipients(managerId: string | null, members: readonly Member[]): string[] {
  const active = members.filter((member) => member.active);
  const told = new Set(active.filter((member) => member.role === "hr").map((member) => member.id));
  if (managerId && active.some((member) => member.id === managerId)) told.add(managerId);
  if (told.size === 0) for (const member of active) if (member.role === "admin") told.add(member.id);
  return [...told];
}

export function contractNoticeText(employee: string, endDate: IsoDate): string {
  return `${employee}'s contract ends ${endDate}: convert them, extend it, or begin their offboarding.`;
}

/* ------------------------------------------------- the temp-to-perm checkpoint -- */

export interface CheckpointRequest {
  decision: "convert" | "extend";
  /** Extend only: the new last contracted day. */
  endDate?: string | null;
}

/**
 * The Day-N prompt for a temp (PRD 6.3, 9.1): convert to full-time, or extend
 * the contract. The third choice, begin offboarding, is starting one (with
 * the reason `temp-not-converted`), so it is not decided here.
 *
 * Only somebody here and temp can be asked. An extension has to move the end
 * date later: extending to a day already contracted would change nothing and
 * look as if it had.
 */
export function checkCheckpoint(
  input: unknown,
  employee: {
    state: EmployeeState;
    employmentStatus: string;
    endDate: IsoDate | null;
    startDate: IsoDate | null;
  },
):
  | { ok: true; decision: "convert" }
  | { ok: true; decision: "extend"; endDate: IsoDate }
  | { ok: false; reason: string } {
  const raw = (input ?? {}) as Record<string, unknown>;
  if (employee.state !== "onboarding" && employee.state !== "active")
    return { ok: false, reason: "Only somebody who is here can be converted or extended." };
  if (employee.employmentStatus !== "temp")
    return { ok: false, reason: "Only a temp can be converted or extended." };
  if (raw.decision === "convert") return { ok: true, decision: "convert" };
  if (raw.decision !== "extend") return { ok: false, reason: "Choose convert or extend." };
  if (!isIsoDate(raw.endDate)) return { ok: false, reason: "Choose the day the extended contract ends." };
  const floor = employee.endDate ?? employee.startDate;
  if (floor && compareDates(raw.endDate, floor) <= 0) {
    return {
      ok: false,
      reason: employee.endDate
        ? "An extension has to end after the current contract does."
        : "Their contract has to end after they start.",
    };
  }
  return { ok: true, decision: "extend", endDate: raw.endDate };
}
